import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { setCurrentUserEmail } from './api';
import Login from './components/login/Login';
import Signup from './components/signup/Signup';
import Addendums from './components/addendums/Addendums';
import AppLayout from './components/layout/AppLayout';
import ReportsHome from './components/reports/ReportsHome';
import ReportView from './components/reports/ReportView';
import Vendors from './components/marketing/Vendors';
import Campaigns from './components/marketing/Campaigns';
import Spends from './components/marketing/Spends';
import AdSync from './components/marketing/AdSync';
import BlockedLeads from './components/blocked/BlockedLeads';
import CanopySync from './components/canopy/CanopySync';
import Splits from './components/commissions/Splits';
import Openers from './components/commissions/Openers';
import Imports from './components/commissions/Imports';
import Earned from './components/commissions/Earned';
import Users from './components/users/Users';
import Profiles from './components/users/Profiles';
import { AccessProvider, useAccess, PERMISSIONS } from './components/auth/AccessContext';
import NoAccess from './components/auth/NoAccess';

// Redirects to the login page unless the user is signed in with a verified
// email. Mirrors the old opener-client-search redirect-on-auth-change pattern.
function ProtectedRoute({ user, authLoading, children }) {
    if (authLoading) return null;
    if (!user || !user.emailVerified) return <Navigate to="/" replace />;
    return children;
}

// Gates one route on a permission. Firebase has already established *who* the
// caller is by this point; this decides what they may open. Rendering is only
// half of it — the matching API calls are refused server-side too, so a
// hand-typed URL reaches a page with no data rather than data it shouldn't have.
function RequirePermission({ permission, children }) {
    const { loading, error, can, reload } = useAccess();
    if (loading) return null;
    // No usable account at all: name which case it is rather than saying the
    // page is off-limits. An error with no code is the access check itself
    // failing (server down, network) — that must not read as "denied".
    if (error) {
        return (
            <NoAccess
                code={error.code || 'unavailable'}
                message={error.message}
                onRetry={error.code ? undefined : reload}
            />
        );
    }
    if (permission && !can(permission)) return <NoAccess />;
    return children;
}

// Where a sign-in lands. It depends on the profile: sending an Intake Agent to
// a report they can't open would greet them with a refusal.
function HomeRedirect() {
    const { loading, error, can, reload } = useAccess();
    if (loading) return null;
    if (error) {
        return (
            <NoAccess
                code={error.code || 'unavailable'}
                message={error.message}
                onRetry={error.code ? undefined : reload}
            />
        );
    }
    if (can(PERMISSIONS.addendum)) return <Navigate to="/addendum" replace />;
    if (can(PERMISSIONS.reports)) return <Navigate to="/reports" replace />;
    if (can(PERMISSIONS.marketing)) return <Navigate to="/marketing" replace />;
    if (can(PERMISSIONS.canopy)) return <Navigate to="/canopy" replace />;
    if (can(PERMISSIONS.commissions)) return <Navigate to="/commissions" replace />;
    if (can(PERMISSIONS.users)) return <Navigate to="/users" replace />;
    return (
        <NoAccess title="Nothing to show yet">
            Your account is set up, but your profile doesn't include any sections yet.
            Ask an admin to grant access.
        </NoAccess>
    );
}

export default function App() {
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (u) => {
            setUser(u);
            setCurrentUserEmail(u ? u.email : '');
            setAuthLoading(false);
        });
        return unsubscribe;
    }, []);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/signup" element={<Signup />} />

                {/* Signed-in area: shared sidebar + top bar chrome via AppLayout,
                    with each page rendered into its <Outlet />. AccessProvider
                    loads the caller's permissions once for everything inside. */}
                <Route
                    element={
                        <ProtectedRoute user={user} authLoading={authLoading}>
                            <AccessProvider>
                                <AppLayout />
                            </AccessProvider>
                        </ProtectedRoute>
                    }
                >
                    <Route path="/home" element={<HomeRedirect />} />

                    <Route
                        path="/addendum"
                        element={<RequirePermission permission={PERMISSIONS.addendum}><Addendums /></RequirePermission>}
                    />

                    <Route
                        path="/reports"
                        element={<RequirePermission permission={PERMISSIONS.reports}><ReportsHome /></RequirePermission>}
                    />
                    <Route
                        path="/reports/:key"
                        element={<RequirePermission permission={PERMISSIONS.reports}><ReportView /></RequirePermission>}
                    />

                    {/* Marketing admin (read/write), separate from the
                        read-only report views above. */}
                    <Route path="/marketing" element={<Navigate to="/marketing/vendors" replace />} />
                    <Route
                        path="/marketing/vendors"
                        element={<RequirePermission permission={PERMISSIONS.marketingVendors}><Vendors /></RequirePermission>}
                    />
                    <Route
                        path="/marketing/campaigns"
                        element={<RequirePermission permission={PERMISSIONS.marketingCampaigns}><Campaigns /></RequirePermission>}
                    />
                    <Route
                        path="/marketing/spends"
                        element={<RequirePermission permission={PERMISSIONS.marketingSpends}><Spends /></RequirePermission>}
                    />
                    <Route
                        path="/marketing/ad-sync"
                        element={<RequirePermission permission={PERMISSIONS.marketingAdSync}><AdSync /></RequirePermission>}
                    />

                    {/* The suppression list: whose website form submissions
                        get dropped instead of becoming a case. */}
                    <Route
                        path="/blocked"
                        element={<RequirePermission permission={PERMISSIONS.blocked}><BlockedLeads /></RequirePermission>}
                    />

                    {/* Canopy Sync: pull an IRS Logics case and create the
                        client in Canopy. */}
                    <Route
                        path="/canopy"
                        element={<RequirePermission permission={PERMISSIONS.canopy}><CanopySync /></RequirePermission>}
                    />

                    {/* Commissions: cases and their split agents, the agents
                        themselves (split agents and settlement officers), the
                        Logics payment report uploads, and what each earned.
                        /commissions/openers was the Agents page's first URL. */}
                    <Route path="/commissions" element={<Navigate to="/commissions/splits" replace />} />
                    <Route
                        path="/commissions/splits"
                        element={<RequirePermission permission={PERMISSIONS.commissions}><Splits /></RequirePermission>}
                    />
                    <Route
                        path="/commissions/agents"
                        element={<RequirePermission permission={PERMISSIONS.commissions}><Openers /></RequirePermission>}
                    />
                    <Route path="/commissions/openers" element={<Navigate to="/commissions/agents" replace />} />
                    <Route
                        path="/commissions/imports"
                        element={<RequirePermission permission={PERMISSIONS.commissions}><Imports /></RequirePermission>}
                    />
                    <Route
                        path="/commissions/earned"
                        element={<RequirePermission permission={PERMISSIONS.commissions}><Earned /></RequirePermission>}
                    />

                    {/* Who may sign in, and what each profile can see. Only
                        reachable from an Admin profile. */}
                    <Route
                        path="/users"
                        element={<RequirePermission permission={PERMISSIONS.users}><Users /></RequirePermission>}
                    />
                    <Route
                        path="/profiles"
                        element={<RequirePermission permission={PERMISSIONS.users}><Profiles /></RequirePermission>}
                    />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}
